import { computed, createApp, h } from 'vue';
import { ref } from 'vue';
import {
    Dialog as HDialog, DialogOverlay as HDialogOverlay, DialogTitle as HDialogTitle, DialogDescription as HDialogDescription,
    Disclosure, DisclosureButton, DisclosurePanel,
    Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions,
    Menu as HMenu, MenuButton as HMenuButton, MenuItems as HMenuItems, MenuItem as HMenuItem,
    Popover, PopoverButton, PopoverGroup, PopoverPanel,
    RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption,
    Switch as HSwitch, SwitchGroup as HSwitchGroup, SwitchLabel as HSwitchLabel,
    TabGroup, TabList, Tab, TabPanels, TabPanel,
    TransitionRoot, TransitionChild,
    } from "@headlessui/vue"

import axios from 'axios'
import { Carousel as VCCarousel, Slide as VCSlide, Pagination as VCPagination, Navigation as VCNavigation } from 'vue3-carousel'
import Chart from 'chart.js'
import GphApiClient from 'giphy-js-sdk-core'
import { gsap } from 'gsap';
import heic2any from 'heic2any'
import LitepieDatepicker from 'litepie-datepicker-tw3';
import moment from 'moment'
import numeral from 'numeral'
import { QuillEditor } from '@vueup/vue-quill'
import RecordRTC from 'recordrtc'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import * as Sentry from "@sentry/vue";

import '@vueup/vue-quill/dist/vue-quill.bubble.css'
import '@vueup/vue-quill/dist/vue-quill.snow.css'
import 'vue3-carousel/dist/carousel.css'

// axios config
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
axios.defaults.headers.common['X-CSRF-TOKEN'] = document.querySelector('meta[name="csrf-token"]').getAttribute('content');
axios.defaults.withCredentials = true;
axios.defaults.withXSRFToken = true;

// gsap
gsap.registerPlugin(ScrollToPlugin);
gsap.registerPlugin(ScrollTrigger);

// moment config
moment.updateLocale('en', {
    week: {
        dow: 1    // set first day of week to Monday
    }
});

// enable spell checker in quill
import Quill from 'quill'
const Inline = Quill.import('blots/inline');
class CustomColor extends Inline {
  constructor(domNode, value) {
    super(domNode, value);

    // Map <font> properties
    domNode.style.color = domNode.color;

    const span = this.replaceWith(new Inline(Inline.create()));

    span.children.forEach(child => {
      if (child.attributes) child.attributes.copy(span);
      if (child.unwrap) child.unwrap();
    });

    this.remove();

    return span;
  }
}

CustomColor.blotName = "customColor";
CustomColor.tagName = "FONT";
Quill.register(CustomColor, true);

let quillOptions = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
            [{ 'align': [] }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image', 'video'],
            ['clean'],
        ]
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
let quillOptionsNoFiles = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            [{ 'align': [] }],
            [{ 'indent': '-1'}, { 'indent': '+1' }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean'],
        ]
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
let quillOptionsOnlyFormatting = {
    contentType: 'QuillEditor',
    modules: {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'header': 1 }, { 'header': 2 }],
            [{ 'size': ['small', false, 'large', 'huge'] }],
            ['clean'],
        ]
    },
    placeholder: 'Start typing here...',
    theme: 'bubble',
    scrollingContainer: 'html',
};
QuillEditor.props.globalOptions.default = () => quillOptions;

// create app
const app = createApp({
    mixins: [appMixins, adminMixins],
    components: {
    },
    data() {
        return {
            vueAppLoading: true,
            isAdminPopoverOpen: false,

            Chart: Chart,
            giphy: GphApiClient('wD3vtsmjViV020rB6fXEEeNXzh03fc3R'),
            gsap: gsap,
            heic2any: heic2any,
            moment: moment,
            numeral: numeral,
            RecordRTC: RecordRTC,
            quillOptionsNoFiles: quillOptionsNoFiles,
            quillOptionsOnlyFormatting: quillOptionsOnlyFormatting,

            darkMode: 2,
            bottomFloatOff: false,
            bottomNavOn: false,
            poweredByPonoFixedOn: true,
            overlayOn: false,
            overlayBottomOn: false,
            scrollDownOn: true,
            treeExpanded: false,
            formTouched: false,
            isHovered: [],
            dashboardSearch: typeof vdashboardSearch !== 'undefined' ? vdashboardSearch : 0,
            carouselActiveIndex: 0,

            adminMenuOpen: [false,false,false,false,false,false,false],
            adminListFilterVisible: false,

            linksOpenInSameTab: true,            // automatically handle link targets to open in the same tab most of the time, see addEventListener in mounted

            modalOpen: [],

            modalOkOpen: false,                  // ok modal
            modalOkTitle: '',
            modalOkContent: '',
            modalOkBtn: '',

            modalWarningOpen: false,             // warning modal
            modalWarningTitle: '',
            modalWarningContent: '',
            modalWarningBtn: '',

            modalConfirmOpen: false,             // confirm modal
            modalConfirmFn: '',
            modalConfirmId1: null,
            modalConfirmId2: null,
            modalConfirmTitle: '',
            modalConfirmContent: '',
            modalConfirmBtn1: '',
            modalConfirmBtn2: '',

            modalNotificationsOpen: false,
            modalNotificationDetailsOpen: false,
            modalNotificationDetailsData: null,
            modalPlaceholdersOpen: false,
            modalPonoupgradeOpen: false,

            modalAsset1Src: '',                  // asset modals
            modalAsset1Name: '',                 // asset modals
            modalAsset1Rotation: 0,
            modalAsset1Open: false,
            modalAsset11Src: '',
            modalAsset11Open: false,
            modalAsset13Converting: false,
            modalAsset13Src: '',
            modalAsset13Open: false,
            modalAsset14Src: '',
            modalAsset14Open: false,
            modalAsset15Src: '',
            modalAsset15Open: false,
            modalAsset16Src: '',
            modalAsset16Open: false,

            passwordInputVisible: true,            // show/hide password in all password input boxes

            toastApp: '',                          // toast notifications
            toastAdmin: '',

            notifications: null,
            notificationsRecent: null,
            notificationTypes: null,
            notificationTypeFilter: null,
            keepInTouchCount: 0,
            keepInTouchOverdueCount: 0,
            visibleNotificationCount: 50,
            unreadNotificationCount: typeof vunreadNotificationCount !== 'undefined' ? vunreadNotificationCount : 0,
            recentNotificationCount: typeof vrecentNotificationCount !== 'undefined' ? vrecentNotificationCount : 0,
            autoMarkNotificationsAsRead: typeof vautoMarkNotificationsAsRead !== 'undefined' ? vautoMarkNotificationsAsRead : 0,

            alertCountDown: 0,                                             // seconds before dismissing b-alert

            acceptBox: null,
            signatureBox: null,

            file_upload: null,
            main_asset: 0,
            preview_asset: 0,
            remove_assets: '',
            uploadAssets: [],
            uploadAssetsCount: 0,
            uploadAssetsRemoved: [],

            addLoom: '',
            uploadLooms: [],
            addLoom2: '',
            uploadLooms2: [],
            addYoutube: '',
            uploadYoutubes: [],
            addYoutube2: '',
            uploadYoutubes2: [],
            addYoutube5: '',
            uploadYoutubes5: [],
            addVimeo: '',
            uploadVimeos: [],
            addVimeo2: '',
            uploadVimeos2: [],
            addVimeo5: '',
            uploadVimeos5: [],
            addWistia: '',
            uploadWistias: [],
            addWistia2: '',
            uploadWistias2: [],
            addWistia5: '',
            uploadWistias5: [],

            scrollY: 0,

            userNotesClicked: [],                        // admin has clicked on a note to expand it
            userNotesDismissed: [],                      // highligted note has been dismissed by admin
            userNotesHidden: false,

            sendMessageVisible: false,

            productOptionTrackInventory: true,
            productOptionInventory: 0,
            productOptionInventoryLoaded: false,
        }
    },
    methods:
    {
        // click on a submenu in the admin menu
        clickAdminMenu(item)
        {
            if (this.adminMenuOpen[item])
            {
                this.adminMenuOpen[item] = false;
            }
            else
            {
                this.adminMenuOpen = [false,false,false,false,false,false,false];
                this.adminMenuOpen[item] = true;
            }
        },

        // click on an item in the admin menu
        clickAdminMenuItem(e, item)
        {
            // show overlay when opening in same window
            try
            {
                if (!e.shiftKey && !e.ctrlKey && !e.metaKey)
                {
                    this.showOverlay();
                    this.hideOverlayWhenLoadingComplete();
                }
            }
            catch(ee) {}
        },

        // show admin filter
        showAdminFilter()
        {
            this.adminListFilterVisible = true;
        },

        // hide admin filter
        hideAdminFilter(url)
        {
            this.adminListFilterVisible = false;

            this.showOverlay();
            this.hideOverlayWhenLoadingComplete();

            location.href = url;
        },

        // choose new avatar
        chooseAvatar()
        {
            document.getElementById('avatar-upload').click();
        },

        // blur focus from all inputs
        blurFocus()
        {
            setTimeout(() => {
                let el = document.querySelector(':focus');
                if (el)
                {
                    el.blur();
                }
            }, 200);
        },

        blurFocusAndStopPropagation(e)
        {
            e.stopPropagation();
            this.blurFocus();
        },

        // Find all Quill editor elements and modify content, to avoid a bug with "<p> <br> </p>"
        cleanQuill()
        {
            try {
                setTimeout(() => {
                    let tEditors = document.querySelectorAll('.ql-editor');
                    tEditors.forEach((tEditor) => {
                        if (tEditor.innerHTML) {
                            tEditor.innerHTML = tEditor.innerHTML.replace(/<p> <br> <\/p>/g, '<p><br></p>').replace(/<p>  <br>  <\/p>/g, '<p><br></p>').replace(/<p>  <\/p>/g, '<p></p>');
                        }
                    });
                }, 100);
            } catch (error) {}
        },

        // toggle dark mode
        toggleDarkMode(value)
        {
            this.darkMode = value;

            if (this.darkMode == 1)
            {
                localStorage.ponoTheme = 'verylight';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 2)
            {
                localStorage.ponoTheme = 'light';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 3)
            {
                localStorage.ponoTheme = 'blue';
                document.documentElement.classList.remove('dark');
            }
            else if (this.darkMode == 4)
            {
                localStorage.ponoTheme = 'dark';
                document.documentElement.classList.add('dark');
            }
            else
            {
                localStorage.ponoTheme = 'auto';
                document.documentElement.classList.remove('dark');

                if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
                {
                    document.documentElement.classList.add('dark');
                }
            }
        },

        // handle scroll events
        scrollListener()
        {
            // show/hide powered by Pono on scroll
            if (window.scrollY > 100)
            {
                this.poweredByPonoFixedOn = false;
            }
            else
            {
                this.poweredByPonoFixedOn = true;
            }

            // show/hide bottom nav links for scroll to top and contact
            if (window.scrollY > 400)
            {
                this.bottomNavOn = true;
                this.scrollDownOn = false;
            }
            else
            {
                this.bottomNavOn = false;
                this.scrollDownOn = true;
            }
        },

        // scroll down
        scrollDown()
        {
            window.scrollTo(0, window.innerHeight);
        },

        // scroll to bottom
        scrollToBottom()
        {
            window.scrollTo(0, 9999999);
        },

        // scroll to top
        scrollToTop()
        {
            window.scrollTo(0, 0);
        },

        // reload page
        reloadPage()
        {
            this.showOverlay();
            window.location.reload();
        },

        // reload page without triggering any POST to repear
        reloadPageViaRedirect()
        {
            this.showOverlay();
            window.location.href = window.location.href;
        },

        // viewing on mobile device
        isMobile()
        {
            return window.innerWidth < 768 ? true : false;
        },

        // check if is numeric
        isNumeric(str) {
            return !isNaN(str) && !isNaN(parseFloat(str));
        },

        // show page overlay
        showOverlay()
        {
            this.overlayOn = true;
        },

        // hide page overlay
        hideOverlay()
        {
            this.overlayOn = false;
        },

        // hide page overlay when broswer has finished loading
        hideOverlayWhenLoadingComplete() {
            this.overlayOn = true;
            setTimeout(() => {
                this.overlayOn = false;
            }, 5000);
        },

        // show top overlay
        showBottomOverlay()
        {
            this.overlayBottomOn = true;
        },

        // hide top overlay
        hideBottomOverlay()
        {
            this.overlayBottomOn = false;
        },

        // auto-dismissing alert
        alertCountdownChanged(alertCountdown)
        {
            this.alertCountdown = alertCountdown;
        },

        // open link
        openLink(e, link, action=0, stopPropagation=false)
        {
            // stop propagation if pressing shift key
            if (e.shiftKey)
            {
                e.stopPropagation();
                return;
            }

            // stop propagation
            if (stopPropagation)
            {
                e.stopPropagation();
            }

            if (!link)
            {
                return;
            }

            if (link.toString().substr(0,4) == 'www.')
            {
                link = 'http://' + link;
            }

            if (action == 3)
            {
                if (link.toString().substr(0,4) == 'http' || link.toString().substr(0,1) == '/')
                {
                    window.open(link);
                }
                else
                {
                    window.open('/'+link);
                }
            }
            else if (action == 2 || (link.toString().substr(0,4) == 'http' && action !== 1 && !link.includes(window.location.origin)))
            {
                window.open(link);
            }
            else
            {
                if (e.ctrlKey || e.metaKey)
                {
                    window.open(link);
                }
                else
                {
                    location.href = link;
                }
            }
        },

        // open link with overlay
        openLinkWithOverlay(e, link, action=0, stopPropagation=false)
        {
            if (!event.ctrlKey && !event.shiftKey && !event.metaKey)
            {
                this.showOverlay();
            }

            setTimeout(() => { this.openLink(e, link, action, stopPropagation); }, 100);
            this.hideOverlayWhenLoadingComplete();
        },

        openUrl(e, url)
        {
            e.stopPropagation();
            window.open(url);
        },

        // copy to clipboard
        copyToClipboard(content, message, toastTimeout=5000)
        {
            let textArea = document.createElement('textarea');
            textArea.value = content;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);

            this.showToastApp(message ? message : 'Copied to the clipboard', toastTimeout);
            this.showToastAdmin(message ? message : 'Copied to the clipboard');

            // remove selection
            if (window.getSelection)
            {
                window.getSelection().removeAllRanges();
            }
            else if (document.selection)
            {
                document.selection.empty();
            }
        },

        // clicked on admin user note to expand/collapse it
        clickOnNote(id) {
            let index = this.userNotesClicked.indexOf(id);
            if (index > -1)
            {
                this.userNotesClicked = [];
            }
            else
            {
                this.userNotesClicked = [];
                this.userNotesClicked.push(id);
            }
        },

        // set active index when scrolling in carousel
        carouselUpdateActiveIndex(slide) {
            this.carouselActiveIndex = slide.slidingToIndex;
        },

        // set datepicker to today
        dateSetToday(vmodel, range)
        {
            if (range)
            {
                this[vmodel] = this.moment().format('DD MMM YYYY') + ' ~ ' + this.moment().format('DD MMM YYYY');
                this.$refs[vmodel].pickerValue = this.moment().format('DD MMM YYYY') + ' ~ ' + this.moment().format('DD MMM YYYY');
            }
            else
            {
                this[vmodel] = this.moment().format('DD MMM YYYY');
                this.$refs[vmodel].pickerValue = this.moment().format('DD MMM YYYY');
            }

            this.touchForm();
        },

        // open notification from the admin notification center
        openNotification(e, notification)
        {
            this.showOverlay();

            // automatically mark as read?
            if (this.autoMarkNotificationsAsRead)
            {
                this.axios.post('/pono-admin/notifications/'+notification.id+'/mark-as-read').then(res => {
                    if (e.ctrlKey || e.metaKey)
                    {
                        this.hideOverlay();
                        window.open(notification.url);
                    }
                    else
                    {
                        location.href = notification.url;
                    }
                });
            }
            else
            {
                if (e.ctrlKey || e.metaKey)
                {
                    this.hideOverlay();
                    window.open(notification.url);
                }
                else
                {
                    location.href = notification.url;
                }
            }
        },

        // mark admin notification as read
        markNotificationAsRead(notification)
        {
            this.axios.post('/pono-admin/notifications/'+notification.id+'/mark-as-read').then(res => {
                if (res && res.data && res.data.success)
                {
                    notification.read_at = new Date();
                    this.getNotificationCount();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // mark admin notification as unread
        markNotificationAsUnread(notification)
        {
            this.axios.post('/pono-admin/notifications/'+notification.id+'/mark-as-unread').then(res => {
                if (res && res.data && res.data.success)
                {
                    notification.read_at = null;
                    this.getNotificationCount();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // mark all admin notification as read
        markAllNotificationAsRead(id)
        {
            this.openModalConfirm('Are you sure?',
                                    'Would you like to mark all notifications as read?',
                                    'Yes, mark all as read now',
                                    'No, do not mark as read',
                                    'markAllNotificationAsReadConfirmed',
                                    id);
        },

        markAllNotificationAsReadConfirmed(id)
        {
            this.showOverlay();
            window.location.href = '/pono-admin/users/' + id + '/notifications/mark-all-as-read';
        },

        // view admin notification detail popup
        viewNotificationDetails(notification)
        {
            this.modalNotificationDetailsData = notification;
            this.modalNotificationDetailsOpen = true;
        },

        // update unread notification count
        getNotificationCount()
        {
            this.axios.post('/pono-admin/load-unread-notifications').then(res => {
                if (res && res.data && res.data.success)
                {
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // called (mostly from admin) when any form element is touched
        touchForm()
        {
            this.formTouched = true;
        },

        // submit form
        submitForm(ref='form')
        {
            // copy quill content
            try
            {
                this.submitFormAdditional();
            }
            catch(e) {}

            this.showOverlay();

            setTimeout(() => { this.$refs[ref].submit(); }, 100);
        },

        // submit form as draft
        submitFormAsDraft()
        {
            this.showOverlay();
            setTimeout(() => {
                document.getElementById('draft_save_for_later').value = 1;
                this.$refs.form.submit();
            }, 100);
        },

        // show/hide admin send message screen
        sendMessage()
        {
            this.sendMessageVisible = true;
            this.adminStatusSummaryOpen = false;

            setTimeout(() => {
                let messageContent = document.getElementById('message-content');
                if (messageContent && messageContent.firstChild) {
                    messageContent.firstChild.focus();
                }
            }, 500);
        },

        // update analytics report visibility
        toggleAnalyticsWidgetVisibility(id)
        {
            if (this.hiddenWidgets.includes(id))
            {
                let index = this.hiddenWidgets.indexOf(id);
                this.hiddenWidgets.splice(index, 1);
            }
            else
            {
                this.hiddenWidgets.push(id);
            }

            this.axios.post('/pono-admin/reports/analytics/store-widget-visibility', {'widget': id, 'hidden': this.hiddenWidgets.includes(id)});
            this.hiddenWidgetsChanged = true;
        },

        // expand/collapse tree
        expandCollapseTree()
        {
            this.treeExpanded = !this.treeExpanded;
        },

        // handle hover
        handleHover(hovered, id)
        {
            this.isHovered[id] = hovered ? 1 : 0;
            this.$forceUpdate();
        },

        // open OK modal
        openModalOk(title, content, btn='')
        {
            this.modalOkTitle = title;
            this.modalOkContent = content;
            this.modalOkBtn = btn;
            this.modalOkOpen = true;
            this.$forceUpdate();
        },

        // close OK modal
        closeModalOk()
        {
            this.modalOkOpen = false;
            this.$forceUpdate();
        },

        // open warning modal
        openModalWarning(title, content, btn='')
        {
            this.modalWarningTitle = title;
            this.modalWarningContent = content;
            this.modalWarningBtn = btn;
            this.modalWarningOpen = true;
            this.$forceUpdate();
        },

        // close warning modal
        closeModalWarning()
        {
            this.modalWarningOpen = false;
            this.$forceUpdate();
        },

        // open confirm modal
        openModalConfirm(title, content, btn1='', btn2='', fn='', id1=null, id2=null)
        {
            this.modalConfirmTitle = title;
            this.modalConfirmContent = content;
            this.modalConfirmBtn1 = btn1;
            this.modalConfirmBtn2 = btn2;
            this.modalConfirmFn = fn;
            this.modalConfirmId1 = id1;
            this.modalConfirmId2 = id2;
            this.modalConfirmOpen = true;
        },

        // close OK modal
        closeModalConfirm(confirmed=false)
        {
            this.modalConfirmOpen = false;

            if (confirmed && this.modalConfirmFn)
            {
                if (this.modalConfirmId1 && this.modalConfirmId2)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1, this.modalConfirmId2);
                }
                else if (this.modalConfirmId1)
                {
                    this[this.modalConfirmFn](this.modalConfirmId1);
                }
                else
                {
                    this[this.modalConfirmFn]();
                }
            }
        },

        // open notifications modal
        openModalNotifications(filter='', removeFilter=false)
        {
            this.notificationTypeFilter = filter;

            this.axios.post('/pono-admin/load-unread-notifications?' + (filter ? 'notification='+filter+'&' : '') + (removeFilter ? 'clearfilter=1' : '')).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.notifications = res.data.notifications;
                    this.notificationsRecent = res.data.recents;
                    this.notificationTypes = res.data.notificationTypes;
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                    this.recentNotificationCount = res.data.recentNotificationCount;
                    this.notificationTypeFilter = res.data.notificationFilter;

                    this.modalNotificationsOpen = true;
                    this.$forceUpdate();
                    this.blurFocus();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // load previous or next page of notifications
        loadPreviousNextModalNotifications(previous=false)
        {
            this.showOverlay();
            this.modalNotificationsOpen = false;
            if (previous)
            {
                this.visibleNotificationCount -= 100;
            }

            this.axios.post('/pono-admin/load-unread-notifications?' + (this.notificationTypeFilter ? 'notification='+this.notificationTypeFilter+'&' : '') + ('from='+this.visibleNotificationCount)).then(res => {
                this.hideOverlay();

                if (res && res.data && res.data.success)
                {
                    this.visibleNotificationCount += 50;

                    this.notifications = res.data.notifications;
                    this.notificationsRecent = res.data.recents;
                    this.notificationTypes = res.data.notificationTypes;
                    this.unreadNotificationCount = res.data.unreadNotificationCount;
                    this.recentNotificationCount = res.data.recentNotificationCount;
                    this.modalNotificationsOpen = true;
                    this.$forceUpdate();
                    this.blurFocus();
                }
                else
                {
                    this.openModalWarning('ERROR','There was a connection problem and the process could not be completed. Please try again.');
                }
            });
        },

        // close notifications modal
        closeModalNotifications()
        {
            this.modalNotificationDetailsOpen = false;
            this.modalNotificationsOpen = false;
            this.$forceUpdate();
        },

        // close notifications modal
        closeModalNotificationDetails()
        {
            this.modalNotificationDetailsOpen = false;
            this.$forceUpdate();
        },

        // open PONO placeholders modal
        openModalPlaceholders()
        {
            this.modalPlaceholdersOpen = true;
            this.$forceUpdate();
        },

        // close PONO placeholders modal
        closeModalPlaceholders()
        {
            this.modalPlaceholdersOpen = false;
            this.$forceUpdate();
        },

        // open Pono upgrade modal
        openModalPonoUpgrade()
        {
            this.modalPonoupgradeOpen = true;
            this.$forceUpdate();
        },

        // close Pono upgrade modal
        closeModalPonoUpgrade()
        {
            this.modalPonoupgradeOpen = false;
            this.$forceUpdate();
        },

        // show modal for assets type 1, images
        openModalAsset1(src, name='', rotation=0)
        {
            this.modalAsset1Src = src;
            this.modalAsset1Name = name;
            this.modalAsset1Rotation = rotation;
            this.modalAsset1Open = true;
            this.$forceUpdate();
        },

        // close asset 1 modal
        closeModalAsset1()
        {
            this.modalAsset1Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 11, youtube videos
        openModalAsset11(src)
        {
            this.modalAsset11Src = src;
            this.modalAsset11Open = true;
            this.$forceUpdate();
        },

        // close asset 11 modal
        closeModalAsset11()
        {
            this.modalAsset11Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 13, website videos
        openModalAsset13(src, converting)
        {
            this.modalAsset13Converting = converting;
            this.modalAsset13Src = src;
            this.modalAsset13Open = true;
            this.$forceUpdate();
        },

        // close asset 13 modal
        closeModalAsset13()
        {
            this.modalAsset13Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 14, vimeo videos
        openModalAsset14(src)
        {
            this.modalAsset14Src = src;
            this.modalAsset14Open = true;
            this.$forceUpdate();
        },

        // close asset 14 modal
        closeModalAsset14()
        {
            this.modalAsset14Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 15, wistia videos
        openModalAsset15(src)
        {
            this.modalAsset15Src = src;
            this.modalAsset15Open = true;
            this.$forceUpdate();
        },

        // close asset 15 modal
        closeModalAsset15()
        {
            this.modalAsset15Open = false;
            this.$forceUpdate();
        },

        // show modal for assets type 16, loom videos
        openModalAsset16(src)
        {
            this.modalAsset16Src = src;
            this.modalAsset16Open = true;
            this.$forceUpdate();
        },

        // close asset 16 modal
        closeModalAsset16()
        {
            this.modalAsset16Open = false;
            this.$forceUpdate();
        },

        // open view specific modal
        openModal(name)
        {
            this.modalOpen[name] = true;
            this.$forceUpdate();
        },

        // close view specific modal
        closeModal(name)
        {
            this.modalOpen[name] = false;
            this.$forceUpdate();
        },

        // fix to open Litepie datepicker in modal
        openDatepicker(name)
        {
            this.$refs[name].isShow = !this.$refs[name].isShow;
        },

        // show toast app
        showToastApp(str, timeout=5000)
        {
            this.toastApp = str;

            setTimeout(() => { this.toastApp = ''; }, timeout);
        },

        // show toast admin
        showToastAdmin(str)
        {
            this.toastAdmin = str;

            setTimeout(() => { this.toastAdmin = ''; }, 5000);
        },

        // make preview asset
        previewAsset(id)
        {
            this.preview_asset = id;
            this.touchForm();
        },

        // remove assets
        removeAsset(id)
        {
            this.remove_assets = this.remove_assets + id + ',';
            this.touchForm();
        },

        // capture a thumbnail of a video
        captureThumbnail(video, index) {
            let canvas = document.getElementById(`video-thumbnail-${index}`);
            if (!canvas) return;

            let ctx = canvas.getContext("2d");

            // Ensure video seeks to a valid frame before capturing
            let videoDuration = video.duration;
            let captureTime = 0.5;

            video.currentTime = captureTime;

            video.addEventListener("seeked", function onSeeked() {
                requestAnimationFrame(() => {
                    canvas.width = video.videoWidth;
                    canvas.height = video.videoHeight;

                    // Draw video frame
                    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

                    // Draw semi-transparent dark overlay
                    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    // Center position for the icon
                    let centerX = canvas.width / 2;
                    let centerY = canvas.height / 2;
                    let radius = Math.min(canvas.width, canvas.height) * 0.15; // 15% of the smallest dimension

                    // Draw semi-transparent black circle behind the icon
                    ctx.beginPath();
                    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI, false);
                    ctx.fillStyle = "rgba(0, 0, 0, 0.3)"; // Black with 30% opacity
                    ctx.fill();

                    // Adjustments for icon centering
                    let textOffsetX = radius * 0.1; // Shift slightly right
                    let textOffsetY = radius * 0.15; // Shift slightly down

                    // Draw Play Icon
                    ctx.fillStyle = "#ffffff"; // White color
                    ctx.font = `bold ${radius * 1.2}px sans-serif`; // Font size scaled to radius
                    ctx.textAlign = "center";
                    ctx.textBaseline = "middle";

                    ctx.fillText("▶", centerX + textOffsetX, centerY + textOffsetY);
                });

                // Reset the video to start AFTER the frame is captured
                setTimeout(() => {
                    video.currentTime = 0;
                }, 50); // Small delay to ensure seek is processed

                video.removeEventListener("seeked", onSeeked);
            });
        },

        // play a video by clicking on the thumbnail
        playVideo(index) {
            let thumbnail = document.getElementById(`video-thumbnail-${index}`);
            let video = document.getElementById(`video-player-${index}`);

            if (thumbnail && video) {
                thumbnail.classList.add('hidden');
                video.classList.remove('hidden');
            }

            video.play();
        },

        heicToJpg(url,id,overlay=false)
        {
            if (overlay)
            {
                this.showOverlay();
            }
            else
            {
                this.showBottomOverlay();
            }

            fetch(url)
                .then((res) => res.blob())
                .then((blob) =>
                    heic2any(
                        {
                            blob,
                            toType: "image/jpeg",
                            quality: 0.5,
                        }
                    )
                )
                .then((conversionResult) => {
                    var t = URL.createObjectURL(conversionResult);
                    if (document.getElementById(id))
                    {
                        document.getElementById(id).src = t;
                    }

                    if (overlay)
                    {
                        this.hideOverlay();
                    }
                    this.hideBottomOverlay();
                })
                .catch((e) => {
                    if (document.getElementById(id))
                    {
                        document.getElementById(id).src = url;
                    }

                    if (overlay)
                    {
                        this.hideOverlay();
                    }
                    this.hideBottomOverlay();
                });
        },

        // choose file for upload by clicking on file_upload input
        fileUploadChoose(n)
        {
            for (let i=1; i<=20; i++)
            {
                if (!this['file_upload'+n+i])
                {
                    document.getElementById('file_upload'+n+i).click();

                    return;
                }
            }
        },

        // add chosen file from file_upload input for upload
        fileUploadChooseAdd(e,n)
        {
            this.touchForm();

            for (let i=1; i<=20; i++)
            {
                // prevent adding double, as on some browsers when dragging/dropping file will have already been added in fileUploadDrop and fileUploadChooseAdd will additionally be triggered
                if (this.uploadAssets[n] && this.uploadAssets[n].includes(e.target.files))
                {
                    return;
                }

                else if (!this['file_upload'+n+i])
                {
                    if (!this.uploadAssets[n])
                    {
                        this.uploadAssets[n] = [];
                    }
                    this.uploadAssets[n].push(e.target.files);

                    this['file_upload'+n+i] = e.target.files;

                    this.uploadAssetsCount += 1;

                    return;
                }
            }
        },

        // drop file for upload
        fileUploadDrop(e,n,accept)
        {
            // validate file types
            if (accept)
            {
                for (let file of e.dataTransfer.files)
                {
                    if (file)
                    {
                        if (accept == 'image/*' && !file.type.includes('image/'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload images (for example, .jpg or .png files).');
                            return;
                        }
                        else if (accept == 'application/pdf' && !file.type.includes('pdf'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload PDFs.');
                            return;
                        }
                        else if (accept == 'text/csv' && !file.type.includes('text') && !file.type.includes('csv'))
                        {
                            this.openModalWarning('ERROR','This type of file is not allowed. Please only upload text/csv files.');
                            return;
                        }
                    }
                }
            }

            this.touchForm();

            for (let i=1; i<=20; i++)
            {
                if (!this['file_upload'+n+i])
                {
                    if (!this.uploadAssets[n])
                    {
                        this.uploadAssets[n] = [];
                    }
                    this.uploadAssets[n].push(e.dataTransfer.files);

                    this['file_upload'+n+i] = e.dataTransfer.files;
                    this.$refs['file_upload'+n+i].files = e.dataTransfer.files;

                    return;
                }
            }
        },

        // remove previously chosen or dropped file from upload
        fileUploadRemove(e,n,file)
        {
            for (let i=1; i<=20; i++)
            {
                if (this['file_upload'+n+i])
                {
                    let pos = 0;
                    for (let item of this['file_upload'+n+i])
                    {
                        if (item && item.name == file.name && item.size == file.size)
                        {
                            this.uploadAssetsRemoved.push(n+'-'+i+'-'+pos);

                            this.uploadAssetsCount -= 1;

                            this.$forceUpdate();
                        }

                        pos++;
                    }
                }

            }
        },

        // format a URL that is being inputted in a form
        formatURLinput(vmodel)
        {
            let url = this[vmodel].toLowerCase().replace(/[^A-Za-z0-9-/\s]/g,'');
            this[vmodel] = url.trim().replace(/ /g, '-');
        },

        // format Loom link
        formatLoomLink(upload='')
        {
            let link = this['addLoom'+upload];
            if (!link)
            {
                return;
            }

            this['addLoom'+upload] = '';

            if (link.includes('loom.com/share'))
            {
                let url = /loom.com\/share\/([a-z0-9]+)/i.exec(link);
                link = url ? url[1] : '';
            }

            this['uploadLooms'+upload].push(link);
        },

        // remove just added Loom video
        removeLoom(link, upload='')
        {
            let index = this['uploadLooms'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadLooms'+upload].splice(index, 1);
            }
        },

        // format youtube link
        formatYoutubeLink(upload='')
        {
            let link = this['addYoutube'+upload];
            if (!link)
            {
                return;
            }

            this['addYoutube'+upload] = '';

            if (link.includes('https://www.youtube.com/watch?v='))
            {
                link = link.replace('https://www.youtube.com/watch?v=', '');
                link = link.split('?')[0];
            }

            if (link.includes('youtube') || link.includes('youtu.be'))
            {
                // Remove query parameters
                link = link.split('?')[0];

                // handle YouTube Shorts format
                if (link.includes('/shorts/'))
                {
                    link = link.replace('/shorts/', '/watch?v=');
                }

                let url = link.split(/(vi\/|v=|\/v\/|youtu\.be\/|\/embed\/)/);
                link = url[2] !== undefined ? url[2].split(/[^0-9a-z_\-]/i)[0] : url[0];
            }

            // check if embeddable
            /*
            try {
                this.axios.post('/assets/has-video-error', {'url': link}).then(res => {
                    console.log(res['data']);
                    if (!res['data'])
                    {

                    }
                });
            } catch (e) {}
            */

            this['uploadYoutubes'+upload].push(link);
        },

        // remove just added youtube video
        removeYoutube(link, upload='')
        {
            let index = this['uploadYoutubes'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadYoutubes'+upload].splice(index, 1);
            }
        },

        // format vimeo link
        formatVimeoLink(upload='')
        {
            let link = this['addVimeo'+upload];
            if (!link)
            {
                return;
            }

            this['addVimeo'+upload] = '';

            if (link.includes('vimeo'))
            {
                let url = /vimeo.*\/(\d+)/i.exec(link);
                link = url ? url[1] : '';
            }

            this['uploadVimeos'+upload].push(link);
        },

        // remove just added vimeo video
        removeVimeo(link, upload='')
        {
            let index = this['uploadVimeos'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadVimeos'+upload].splice(index, 1);
            }
        },

        // format wistia link
        formatWistiaLink(upload='')
        {
            let link = this['addWistia'+upload];
            if (!link)
            {
                return;
            }

            this['addWistia'+upload] = '';

            let t = /[^/]*$/.exec(link);
            if (t.length > 0)
            {
                link = t[0];
            }

            this['uploadWistias'+upload].push(link);
        },

        // remove just added wistia video
        removeWistia(link, upload='')
        {
            let index = this['uploadWistias'+upload].indexOf(link);
            if (index > -1)
            {
                this['uploadWistias'+upload].splice(index, 1);
            }
        },

        // update quill editor box content when saving
        // mp++ this needs to be implemented for all quill boxes
        updateQuill(id, target)
        {
            if (target.includes('.'))
            {
                let t = target.split('.');
                let t1 = document.getElementById(id);
                if (t1)
                {
                    this[t[0]][t[1]] = t1.querySelector('.ql-editor').innerHTML;

                    if (this[t[0]][t[1]] == '<p><br></p>')
                    {
                        this[t[0]][t[1]] = null;
                    }
                }
            }
            else
            {
                let t = document.getElementById(id);
                if (t)
                {
                    this[target] = t.querySelector('.ql-editor').innerHTML;

                    if (this[target] == '<p><br></p>')
                    {
                        this[target] = null;
                    }
                }
            }
        },

        // increase value of +/- button in a form/journal field
        increasePlusMinusValue(name, min=0, max=0)
        {
            this.touchForm();

            if (!this.pForm[name] || isNaN(this.pForm[name]))
            {
                this.pForm[name] = parseInt(0);
            }

            if (max == 0 || parseInt(this.pForm[name]) < max)
            {
                this.pForm[name] = parseInt(this.pForm[name]) + 1;
            }
        },

        // decrease value of +/- button in a form/journal field
        decreasePlusMinusValue(name, min=0, max=0)
        {
            this.touchForm();

            if (!this.pForm[name] || isNaN(this.pForm[name]))
            {
                this.pForm[name] = parseInt(0);
            }

            if (parseInt(this.pForm[name]) > min)
            {
                this.pForm[name] = parseInt(this.pForm[name]) - 1;
            }
        },

        // increase value of +/- button in the admin via inputPlusminus
        increasePlusMinusValueAdmin(name, min=0, max=0, touchform=1)
        {
            if (touchform) { this.touchForm(); }

            if (!this[name] || isNaN(this[name]))
            {
                this[name] = parseInt(0);
            }

            if (max == 0 || parseInt(this[name]) < max)
            {
                this[name] = parseInt(this[name]) + 1;
            }
        },

        // decrease value of +/- button in the admin via inputPlusminus
        decreasePlusMinusValueAdmin(name, min=0, max=0, touchform=1)
        {
            if (touchform) { this.touchForm(); }

            if (!this[name] || isNaN(this[name]))
            {
                this[name] = parseInt(0);
            }

            if (parseInt(this[name]) > min)
            {
                this[name] = parseInt(this[name]) - 1;
            }
        },

        // check product option inventory
        checkProductOptionInventory(id)
        {
            this.axios.post('/product-option-inventory/'+id, {}).then(res => {
                if (res && res.data && res.data.success)
                {
                    this.productOptionTrackInventory = res.data.track_inventory ? true : false;
                    this.productOptionInventory = res.data.inventory;
                    this.productOptionInventoryLoaded = true;
                }
                else
                {
                    this.productOptionTrackInventory = false;
                    this.productOptionInventoryLoaded = false;
                }
            });
        },

        // show/hide password in password input boxes
        showHidePassword()
        {
            this.passwordInputVisible = !this.passwordInputVisible;
        },

        // create clickable links within text
        convertTextToClickableLinks(str)
        {
            var res, pattern1, pattern2, pattern3, pattern4;

            // match all http, https and ftp
            pattern1 = /(\b(https|ftp):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gim;
            res = str.replace(pattern1, '<a class="link" href="$1" target="_blank">$1</a>');

            // match all www
            pattern2 = /(^|[^\/])(www\.[\S]+(\b|$))/gim;
            res = res.replace(pattern2, '$1<a class="link" href="http://$2" target="_blank">$2</a>');

            // match all with just domain name and ending
            pattern3 = /(\s)([a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})\b/gim;
            res = res.replace(pattern3, '$1<a class="link" href="http://$2" target="_blank">$2</a>');

            // match all email
            pattern4 = /(([a-zA-Z0-9\-\_\.])+@[a-zA-Z\_]+?(\.[a-zA-Z]{2,6})+)/gim;
            res = res.replace(pattern4, '<a class="link" href="mailto:$1">$1</a>');

            return res;
        },
    },
    mounted: function ()
    {
        // add event listener so that all internal links open in same tab
        try
        {
            addEventListener('click', (event) =>
            {
                if (this.linksOpenInSameTab) {
                    let el = event.target;
                    if (el.tagName === 'A' && !el.isContentEditable && (!el.host || el.host == window.location.host))
                    {
                        el.setAttribute('target', '');
                    }
                    else if ((el.parentElement && el.parentElement.tagName === 'A') && !el.parentElement.isContentEditable && (!el.parentElement.host || el.parentElement.host == window.location.host))
                    {
                        el.parentElement.setAttribute('target', '');
                    }
                }
            }, true);
        }
        catch (e) {}

        // load keep in touch
        try
        {
            if (location.href.includes('/pono-admin'))
            {
                this.axios.post('/pono-admin/dashboard/keep-in-touch-count').then(res => {
                    if (res && res.data && res.data.success)
                    {
                        this.keepInTouchCount = res.data.count;
                    }
                });

                this.axios.post('/pono-admin/dashboard/keep-in-touch-overdue-count').then(res => {
                    if (res && res.data && res.data.success)
                    {
                        this.keepInTouchOverdueCount = res.data.count;
                    }
                });
            }
        }
        catch (e) {}
    },
    created: function ()
    {
        this.modalOpen['modal-flash-error'] = true;
        this.modalOpen['modal-flash-success'] = true;

        // set dark mode
        try
        {
            if (localStorage && localStorage.ponoTheme === 'verylight')
            {
                this.darkMode = 1;
            }
            else if (localStorage && localStorage.ponoTheme === 'light')
            {
                this.darkMode = 2;
            }
            else if (localStorage && localStorage.ponoTheme === 'blue')
            {
                this.darkMode = 3;
            }
            else if (localStorage && localStorage.ponoTheme === 'dark')
            {
                this.darkMode = 4;
            }
            else
            {
                this.darkMode = 5;
            }
        }
        catch (e) {}

        this.vueAppLoading = false;

        window.addEventListener('scroll', this.scrollListener);
    },
    destroyed: function ()
    {
        window.removeEventListener('scroll', this.scrollListener);
    },
});

// add Tailwind components, rename some "H" for usage in Laravel screens
app.component('disclosure', Disclosure);
app.component('disclosurebutton', DisclosureButton);
app.component('disclosurepanel', DisclosurePanel);
app.component('hdialog', HDialog);
app.component('hdialogoverlay', HDialogOverlay);
app.component('hdialogtitle', HDialogTitle);
app.component('hdialogdescription', HDialogDescription);
app.component('listbox', Listbox);
app.component('listboxbutton', ListboxButton);
app.component('listboxlabel', ListboxLabel);
app.component('listboxoption', ListboxOption);
app.component('listboxoptions', ListboxOptions);
app.component('hmenu', HMenu);
app.component('hmenubutton', HMenuButton);
app.component('hmenuitems', HMenuItems);
app.component('hmenuitem', HMenuItem);
app.component('popover', Popover);
app.component('popoverbutton', PopoverButton);
app.component('popovergroup', PopoverGroup);
app.component('popoverpanel', PopoverPanel);
app.component('radiogroup', RadioGroup);
app.component('radiogroupdescription', RadioGroupDescription);
app.component('radiogrouplabel', RadioGroupLabel);
app.component('radiogroupoption', RadioGroupOption);
app.component('hswitch', HSwitch);
app.component('hswitchgroup', HSwitchGroup);
app.component('hswitchlabel', HSwitchLabel);
app.component('tabgroup', TabGroup);
app.component('tablist', TabList);
app.component('tab', Tab);
app.component('tabpanels', TabPanels);
app.component('tabpanel', TabPanel);
app.component('transitionroot', TransitionRoot);
app.component('transitionchild', TransitionChild);

// add other components
app.component('ref', ref);
app.component('giphy-js-sdk-core', GphApiClient);
app.component('litepie-datepicker', LitepieDatepicker);
app.component('quilleditor', QuillEditor)
app.component('vccarousel', VCCarousel)
app.component('vcslide', VCSlide)
app.component('vcpagination', VCPagination)
app.component('vcnavigation', VCNavigation)

// Sentry
Sentry.init({
  app,
  dsn: "https://74854fea9e7ec3041dbb8e1705532999@o4508287214944256.ingest.us.sentry.io/4508444177727488",
  ignoreErrors: ["Can't find variable: gmo"],
  integrations: [
    Sentry.replayIntegration(),
  ],
  replaysSessionSampleRate: 0,    // disabled
  replaysOnErrorSampleRate: 0,    // disabled
  beforeSend(event) {
    event.tags = {
      ...event.tags,
      site: window.location.hostname,
    };
    return event;
  },
  enabled: window.location.hostname !== 'pono.test',
});

// add ability for styles in Blade templates
app.component('vstyle',
{
    render: function ()
    {
        return h('style', this.$slots.default())
    }
});

// configs
app.config.devtools = false;
app.config.productionTip = false;
app.config.globalProperties.axios = axios;

// mount app
app.mount("#app");